import {
  type ColumnsType,
  ConfirmModal,
  Loading,
  Table,
  TableRowMenuCell,
  Text,
  ToggleAllButton,
} from '@/components';
import { useMasterTablePermission } from '@/features/authentication/_hooks/permission';
import { useExpandedRowKeys } from '@/hooks';
import { useMessage } from '@/providers';
import {
  TABLE_CODE_WIDTH,
  TABLE_DATETIME_WIDTH,
  TABLE_ROW_MENU_WIDTH,
  TABLE_YEAR_WIDTH,
  datetimeFormat,
  findUpperRecursive,
  getRecursiveArray,
  getRecursiveIds,
} from '@/utils';
import dayjs from 'dayjs';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import {
  useCreateExpenditureNature,
  useDeleteExpenditureNature,
  useExpenditureNatures,
  useNationalExpenditureNatures,
  useUpdateExpenditureNature,
} from '../../hooks';
import type { ExpenditureNatureBaseModel } from '../../models';
import {
  DEFAULT_PAGINATION_SETTING,
  fillZeroToExpenditureNature,
} from '../../utils';
import { useDrawerState } from '../DrawerStateProvider';
import { ExpenditureNatureFormDrawer } from './ExpenditureNatureFormDrawer';

export const ExpenditureNaturesPage: FC = () => {
  const { t } = useTranslation();
  const { drawerState, setDrawerState } = useDrawerState();
  const { openMessage } = useMessage();
  const expenditureNatures = useExpenditureNatures();
  const nationalExpenditureNature = useNationalExpenditureNatures();
  const { mutateAsync: createMutateAsync } = useCreateExpenditureNature();
  const { mutateAsync: updateMutateAsync } = useUpdateExpenditureNature();
  const { mutateAsync: deleteMutateAsync } = useDeleteExpenditureNature();
  const editPermission = useMasterTablePermission(true);
  const {
    expandedRowKeys,
    addExpandedRowKeys,
    setExpandedRowKeys,
    onExpand,
    clearExpandedRowKeys,
  } = useExpandedRowKeys();
  const [expenditureNatureDelete, setExpenditureNatureDelete] = useState<
    string | null
  >(null);

  if (
    expenditureNatures.isLoading ||
    !expenditureNatures.data ||
    nationalExpenditureNature.isLoading ||
    !nationalExpenditureNature.data
  ) {
    return <Loading />;
  }

  const selected = expenditureNatures.data.find(
    (d) =>
      drawerState.open &&
      drawerState.type === 'expenditure-nature' &&
      drawerState.isEditing &&
      d.id === drawerState.id,
  );

  const recursiveExpenditureNatures = getRecursiveArray(
    expenditureNatures.data.filter((d) => !d.upperId),
    expenditureNatures.data,
  );

  const mergedExpenditureNatures = nationalExpenditureNature.data.map((d) => {
    const data = recursiveExpenditureNatures.filter(
      (ep) => ep.nationalExpenditureNatureId === d.id,
    );
    return {
      ...d,
      data,
    };
  });

  const openAllExpanded = () => {
    setExpandedRowKeys(
      mergedExpenditureNatures.reduce<string[]>((acc, cur) => {
        if (cur.data.length === 0) {
          return acc;
        }
        acc.push(cur.id);
        acc.push(...getRecursiveIds(cur.data));
        return acc;
      }, []),
    );
  };

  const handleCloseDrawer = async () => {
    setDrawerState({ open: false });
  };

  const LevelLabelModifier = {
    0: t('expenditure-nature-level-1'),
    1: t('expenditure-nature-level-2'),
    2: t('expenditure-nature-level-3'),
  } as const satisfies Record<
    z.infer<typeof ExpenditureNatureBaseModel.shape.level>,
    string
  >;

  return (
    <>
      <Table
        enableSticky={false}
        dataSource={mergedExpenditureNatures}
        rowKey="id"
        title={() => (
          <ToggleAllButton
            isExpandedAllWhenMounted
            openAll={openAllExpanded}
            closeAll={clearExpandedRowKeys}
          />
        )}
        columns={[
          {
            title: t('code'),
            key: 'code',
            dataIndex: 'code',
            width: TABLE_CODE_WIDTH,
            render: (_, record) => fillZeroToExpenditureNature(record.code),
          },
          {
            title: t('name'),
            key: 'name',
            dataIndex: 'name',
          },
        ]}
        expandable={{
          expandedRowKeys,
          onExpand: (expanded, record) => {
            onExpand(expanded, record.id);
          },
          expandedRowRender: (record) => (
            <Table
              enableSticky={false}
              style={{ marginLeft: 80 }}
              dataSource={record.data}
              rowKey="id"
              columns={[
                {
                  title: t('code'),
                  key: 'code',
                  dataIndex: 'code',
                  width: 110,
                  render: (_, record) =>
                    fillZeroToExpenditureNature(
                      record.code,
                      record.level === 2,
                    ),
                },
                {
                  title: t('level'),
                  key: 'level',
                  dataIndex: 'level',
                  width: 100,
                  render: (_, record) => LevelLabelModifier[record.level],
                },
                {
                  title: t('name'),
                  key: 'name',
                  dataIndex: 'name',
                  width: 240,
                },
                {
                  title: t('start-fiscal-year'),
                  key: 'startYear',
                  dataIndex: 'startYear',
                  width: TABLE_YEAR_WIDTH,
                },
                {
                  title: t('end-fiscal-year'),
                  key: 'endYear',
                  dataIndex: 'endYear',
                  width: TABLE_YEAR_WIDTH,
                },
                {
                  title: t('expenditure-nature-external-system-code'),
                  key: 'externalSystemCode',
                  dataIndex: 'externalSystemCode',
                  width: 200,
                },
                {
                  title: t('aggregation-target-expenditure-nature'),
                  key: 'aggregateByExpenditureNatureForExternalSystem',
                  width: 200,
                  dataIndex: 'aggregateByExpenditureNatureForExternalSystem',
                  render: (value) => (
                    <Text text={value ? t('target') : undefined} />
                  ),
                },
                {
                  title: t('aggregation-name'),
                  key: 'aggregationName',
                  width: 200,
                  dataIndex: 'aggregationName',
                  render: (value) => <Text text={value} />,
                },
                {
                  title: t('created-datetime'),
                  key: 'createdAt',
                  dataIndex: 'createdAt',
                  width: TABLE_DATETIME_WIDTH,
                  render: (value) => datetimeFormat({ datetime: value }),
                },
                {
                  title: t('updated-datetime'),
                  key: 'updatedAt',
                  dataIndex: 'updatedAt',
                  width: TABLE_DATETIME_WIDTH,
                  render: (value) => datetimeFormat({ datetime: value }),
                },
                ...(editPermission.data
                  ? ([
                      {
                        title: '',
                        key: 'actions',
                        fixed: 'right' as const,
                        width: TABLE_ROW_MENU_WIDTH,
                        onCell: () => ({
                          style: { minWidth: TABLE_ROW_MENU_WIDTH },
                        }),
                        render: (_, record) => (
                          <TableRowMenuCell
                            onEdit={() => {
                              setDrawerState({
                                open: true,
                                type: 'expenditure-nature',
                                isEditing: true,
                                id: record.id,
                              });
                            }}
                            items={[
                              ...(record.level === 1
                                ? [
                                    {
                                      key: 'createNextLevelExpenditureNature',
                                      label: t('create-item', {
                                        item: t('expenditure-nature-level-3'),
                                      }),
                                      onClick: () => {
                                        setDrawerState({
                                          open: true,
                                          type: 'expenditure-nature',
                                          isEditing: false,
                                          creatingLevel: 2,
                                          upperId: record.id,
                                        });
                                      },
                                    },
                                  ]
                                : []),
                              {
                                key: 'delete',
                                label: t('delete'),
                                danger: true,
                                onClick: () =>
                                  setExpenditureNatureDelete(record.id),
                              },
                            ]}
                          />
                        ),
                      },
                    ] satisfies ColumnsType<(typeof record.data)[number]>)
                  : []),
              ]}
              expandable={{
                expandedRowKeys,
                onExpand: (expanded, record) => {
                  onExpand(expanded, record.id);
                },
              }}
              pagination={false}
            />
          ),
        }}
        pagination={DEFAULT_PAGINATION_SETTING}
      />

      <ExpenditureNatureFormDrawer
        {...(drawerState.open && drawerState.isEditing
          ? {
              type: 'edit',
              initialValues: {
                ...selected,
                period: [
                  selected?.startYear
                    ? dayjs().year(selected.startYear)
                    : undefined,
                  selected?.endYear
                    ? dayjs().year(selected.endYear)
                    : undefined,
                ],
              },
            }
          : {
              type: 'new',
              initialValues:
                drawerState.open &&
                !drawerState.isEditing &&
                drawerState.type === 'expenditure-nature'
                  ? {
                      level: drawerState.creatingLevel,
                      upperId: drawerState.upperId,
                    }
                  : undefined,
            })}
        onSubmit={async (values) => {
          if (!drawerState.open) {
            return null;
          }
          const res = drawerState.isEditing
            ? await updateMutateAsync({ ...values, id: drawerState.id })
            : await createMutateAsync(values);

          await Promise.all([
            openMessage(
              'success',
              drawerState.isEditing
                ? t('message-update-completed')
                : t('message-create-completed'),
            ),
            handleCloseDrawer(),
            !drawerState.isEditing &&
              (async () => {
                if (res.upperId) {
                  const uppers = findUpperRecursive(
                    res.upperId,
                    expenditureNatures.data,
                  );

                  addExpandedRowKeys([res.id, ...uppers.map((d) => d.id)]);
                }
              })(),
          ]);
        }}
      />
      <ConfirmModal
        open={expenditureNatureDelete !== null}
        title={t('delete-confirmation')}
        disclaimerText={t('delete-confirmation-disclaimer')}
        onOk={async () => {
          invariant(expenditureNatureDelete, t('unreachable-code'));
          try {
            await deleteMutateAsync(expenditureNatureDelete);
            await Promise.all([
              openMessage('success', t('message-delete-completed')),
              (async () => {
                setExpenditureNatureDelete(null);
              })(),
            ]);
          } catch {
            await openMessage('error', t('message-delete-failed'));
          }
        }}
        onCancel={() => setExpenditureNatureDelete(null)}
        skipCheck
      />
    </>
  );
};
